import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { getPromisedTime, type PromisedTimeSuggestion } from '@/lib/api/tickets.js';
import { formatDateTime } from '@/lib/format.js';

type Props = {
  serviceIds: string[];
  assignedToId?: string;
  value: string;
  onChange: (datetimeLocal: string) => void;
};

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function confidenceBadge(level: PromisedTimeSuggestion['confidenceLevel']) {
  if (level === 'high') return '🟢 High confidence';
  if (level === 'medium') return '🟡 Medium confidence';
  return '🔴 Low confidence';
}

export function PromisedTimePanel({ serviceIds, assignedToId, value, onChange }: Props) {
  const query = useQuery({
    queryKey: ['promised-time', serviceIds.join(','), assignedToId ?? ''],
    queryFn: () =>
      getPromisedTime({
        serviceIds: serviceIds.length ? serviceIds : undefined,
        assignedToId,
      }),
    enabled: true,
  });

  const suggestion = query.data?.data;

  function applySuggested() {
    if (!suggestion) return;
    onChange(toDatetimeLocal(suggestion.suggestedAt));
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-800">
          Suggested Pickup Time
        </h3>
      </div>

      {query.isLoading ? (
        <p className="mt-3 text-sm text-slate-500">Calculating from queue & store hours…</p>
      ) : query.isError || !suggestion ? (
        <p className="mt-3 text-sm text-slate-500">Could not load suggestion. Set a custom time below.</p>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-lg font-semibold text-slate-950">
            {formatDateTime(suggestion.suggestedAt)}{' '}
            <span className="text-sm font-medium text-slate-600">
              {confidenceBadge(suggestion.confidenceLevel)}
            </span>
          </p>
          <p className="text-xs text-slate-500">{suggestion.reasoning}</p>
          <p className="text-xs text-slate-400">
            Pickup is estimated in store business hours (not plain clock time). Work past closing
            continues at the next open morning.
          </p>
          <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-slate-500">Repair</dt>
              <dd className="font-semibold">{suggestion.breakdown.repairMinutes} min</dd>
            </div>
            <div>
              <dt className="text-slate-500">Queue wait</dt>
              <dd className="font-semibold">{suggestion.breakdown.queueWaitMinutes} min</dd>
            </div>
            <div>
              <dt className="text-slate-500">Buffer</dt>
              <dd className="font-semibold">{suggestion.breakdown.bufferMinutes} min</dd>
            </div>
            <div>
              <dt className="text-slate-500">Total</dt>
              <dd className="font-semibold">{suggestion.breakdown.totalMinutes} min</dd>
            </div>
          </dl>
          {suggestion.alternatives.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500">Alternatives</p>
              <ul className="mt-1 space-y-1 text-sm text-slate-700">
                {suggestion.alternatives.map((alt) => (
                  <li key={alt.label}>
                    {alt.label}: {formatDateTime(alt.suggestedAt)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-xl bg-primary px-3 py-1.5 text-sm font-semibold text-white"
              onClick={applySuggested}
            >
              Use Suggested Time
            </button>
          </div>
        </div>
      )}

      <label className="mt-4 block text-sm font-medium text-slate-700">
        Promised time
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </section>
  );
}
